import type { LocationTreeNode } from '../../../../api/schedulingApis';
import type { AssignLocationOption } from './assignUsersTypes';

/** Flatten planning location tree for Assign selects (name + ancestor breadcrumb). */
export function flattenAssignLocationOptions(
  nodes: LocationTreeNode[] | null | undefined,
  ancestors: string[] = [],
  depth = 0,
): AssignLocationOption[] {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const out: AssignLocationOption[] = [];
  for (const node of nodes) {
    const id = String(node.location_id ?? '').trim();
    if (!id) continue;
    const name = String(node.name ?? id).trim() || id;
    out.push({
      value: id,
      label: name,
      depth,
      breadcrumb: ancestors.length > 0 ? ancestors.join(' › ') : undefined,
    });
    if (Array.isArray(node.child) && node.child.length > 0) {
      out.push(
        ...flattenAssignLocationOptions(node.child, [...ancestors, name], depth + 1),
      );
    }
  }
  return out;
}

/** Map location id → full path including self (`Tower A › Floor 1`). */
export function buildLocationPathById(
  options: AssignLocationOption[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const opt of options) {
    const path = opt.breadcrumb ? `${opt.breadcrumb} › ${opt.label}` : opt.label;
    map.set(opt.value, collapseRepeatedPathSegments(path));
  }
  return map;
}

/** Drop consecutive duplicate segment names (`Project › Project › A` → `Project › A`). */
export function collapseRepeatedPathSegments(path: string, sep = ' › '): string {
  const parts = String(path ?? '')
    .split(sep)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return '';
  const out: string[] = [];
  for (const part of parts) {
    if (out.length > 0 && out[out.length - 1].toLowerCase() === part.toLowerCase()) {
      continue;
    }
    out.push(part);
  }
  return out.join(sep);
}
